import {
    useContext,
    type FC
} from "react";
import type IRadarChartProps from "./type";
import type {
    RadarChartLayout
} from "./type";
import stylesheet, {
    RADAR_CHART_MULTI_FILL_RATIO,
    getRadarChartMeasures,
    RADAR_CHART_LEVELS,
    useStyles
} from "./stylesheet";
import {
    getRadarChartPolygonPath,
    getRadarChartUnpacked,
    getRadarChartLayout,
    getRadarChartPacked,
    getRadarChartPoint,
    getRadarChartIndex
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import Chart, {
    useChartValueTransition,
    getChartStaggerProgress,
    useChartSeries,
    useChartModel
} from "../chart";
import type {
    ChartTooltipContent,
    ChartPlotLayout,
    ChartTooltipRow
} from "../chart/type";
import {
    Text as SvgText,
    Circle,
    Path,
    Line,
    G
} from "react-native-svg";
import {
    formatNumber
} from "../../helpers/locale";
import {
    getChartNiceScale
} from "../chart/util";
import {
    ChartContext
} from "../chart/context";

type RadarChartDrawable = {
    points: Array<{ x: number; y: number }>;
    areaColor: string;
    opacity: number;
    isLive: boolean;
    color: string;
    key: string;
    d: string;
};

const RadarChart: FC<IRadarChartProps> = ({
    isAnimatedOnMount: isAnimatedOnMountProp,
    customLocalize: customLocalizeProp,
    valueFormatter: valueFormatterProp,
    customTheme: customThemeProp,
    seriesKeys: seriesKeysProp,
    gridShape = "polygon",
    plotSize = "medium",
    isShowDots = false,
    categoryFormatter,
    tooltipBehaviour,
    isFilled = true,
    onActiveChange,
    categoryTitle,
    renderTooltip,
    categoryKey,
    maxValue,
    summary,
    style,
    data
}) => {
    const context = useContext(ChartContext);

    const customTheme = customThemeProp ?? context?.customTheme;

    const customLocalize = customLocalizeProp ?? context?.customLocalize;

    const {
        shapeConfigs,
        typography,
        durations,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const seriesKeys = seriesKeysProp ?? (context ? Object.keys(context.config) : Object.keys(data[0] ?? {}).filter((key) => key !== categoryKey));

    const valueFormatter = valueFormatterProp ?? ((value: number) => formatNumber({
        options: {
            maximumFractionDigits: 2
        },
        locale: activeLocale,
        value
    }));

    useChartModel({
        model: {
            categoryFormatter,
            valueFormatter,
            categoryTitle,
            kind: "radar",
            categoryKey,
            seriesKeys,
            data
        }
    });

    const {
        series
    } = useChartSeries({
        seriesKeys
    });

    const {
        activeLabel: activeLabelColor,
        label: labelColor,
        spoke: spokeColor,
        grid: gridColor
    } = useStyles({
        colors
    });

    const measures = getRadarChartMeasures({
        shapeConfigs,
        typography,
        borders,
        spaces
    });

    const getCategoryText = (index: number): string => {
        const value = data[index]?.[categoryKey] ?? index;

        return categoryFormatter ? categoryFormatter(value, index) : String(value);
    };

    const getValue = (key: string, index: number): number => {
        return Math.max(0, Number(data[index]?.[key]) || 0);
    };

    const categories = data.map((_, index) => getCategoryText(index));

    const matrix = series.map((item) => data.map((_, index) => getValue(item.key, index)));

    const activeSeries = series.filter((item) => !item.isHidden);

    const dataMax = Math.max(0, ...series.flatMap((item, itemIndex) => item.isHidden ? [] : matrix[itemIndex] ?? []));

    const domain = maxValue ?? (getChartNiceScale({
        count: RADAR_CHART_LEVELS,
        max: dataMax,
        min: 0
    }).max || 1);

    const transition = useChartValueTransition({
        shapeKey: `${seriesKeys.join("|")}:${categories.join("|")}`,
        values: getRadarChartPacked({
            values: matrix,
            domain
        })
    });

    const getLayout = ({
        height,
        isRTL,
        width
    }: Pick<ChartPlotLayout, "height" | "isRTL" | "width">): RadarChartLayout => getRadarChartLayout({
        labels: categories,
        measures,
        height,
        isRTL,
        width
    });

    const getTooltipRows = (index: number): Array<ChartTooltipRow> => {
        return activeSeries.map((item): ChartTooltipRow => ({
            value: valueFormatter(getValue(item.key, index)),
            shape: isFilled ? "square" : "line",
            colorIndex: item.colorIndex,
            title: item.title,
            key: item.key
        }));
    };

    const getTooltip = (index: number): ChartTooltipContent => ({
        title: getCategoryText(index),
        rows: getTooltipRows(index)
    });

    const getReading = (index: number): string => {
        const rows = getTooltipRows(index).map((row) => `${row.title} ${row.value}`).join(", ");

        return `${getCategoryText(index)}: ${rows}`;
    };

    const getHighest = (): string => {
        const totals = categories.map((_, index) => Math.max(0, ...activeSeries.map((item) => getValue(item.key, index))));

        const highest = Math.max(0, ...totals);

        const index = totals.indexOf(highest);

        if(index < 0) {
            return "";
        }

        return localize("chart-highest-value", [
            getCategoryText(index),
            valueFormatter(highest)
        ]);
    };

    const currentSummary = summary ?? [
        context?.title,
        context?.description,
        getHighest()
    ].filter(Boolean).join(". ");

    const getIndex: Parameters<typeof Chart.Plot>[0]["getIndex"] = (point, layout) => {
        if(point.x < 0 || point.y < 0 || point.x > layout.width || point.y > layout.height) {
            return undefined;
        }

        return getRadarChartIndex({
            layout: getLayout(layout),
            count: categories.length,
            isRTL: layout.isRTL,
            point
        });
    };

    const getAnchor: Parameters<typeof Chart.Plot>[0]["getAnchor"] = (index, _seriesIndex, layout) => {
        const geometry = getLayout(layout);

        const outer = Math.max(0, ...activeSeries.map((item) => getValue(item.key, index)));

        return getRadarChartPoint({
            radius: geometry.radius * Math.min(1, outer / domain),
            layout: geometry,
            index
        });
    };

    const getDrawables = ({
        geometry,
        layout
    }: {
        geometry: RadarChartLayout;
        layout: ChartPlotLayout;
    }): Array<RadarChartDrawable> => {
        const layers = [
            transition.previous ? {
                opacity: transition.previous.opacity,
                values: transition.previous.values,
                isLive: false
            } : undefined,
            {
                opacity: transition.opacity,
                values: transition.values,
                isLive: true
            }
        ];

        return layers.flatMap((layer) => {
            if(!layer || layer.opacity <= 0) {
                return [];
            }

            const {
                values,
                shape
            } = getRadarChartUnpacked(layer.values);

            return values.flatMap((row, seriesIndex): Array<RadarChartDrawable> => {
                const item = series[seriesIndex];

                if(!item || item.opacity <= 0) {
                    return [];
                }

                const grow = getChartStaggerProgress({
                    progress: layout.entrance.progress,
                    stagger: durations.stagger,
                    count: shape.seriesCount,
                    duration: durations.long,
                    index: seriesIndex
                });

                const points = row.map((value, index) => getRadarChartPoint({
                    radius: geometry.radius * Math.min(1, value / shape.domain) * grow,
                    layout: geometry,
                    index
                }));

                return [
                    {
                        key: `${layer.isLive ? "live" : "previous"}-${item.key}`,
                        opacity: item.opacity * layer.opacity,
                        d: getRadarChartPolygonPath(points),
                        areaColor: item.areaColor,
                        isLive: layer.isLive,
                        color: item.color,
                        points
                    }
                ];
            });
        });
    };

    const renderGrid = (geometry: RadarChartLayout) => {
        if(gridShape === "none" || geometry.radius <= 0) {
            return null;
        }

        const rings = Array.from({
            length: RADAR_CHART_LEVELS
        }, (_, level) => geometry.radius * (level + 1) / RADAR_CHART_LEVELS);

        return <G
            aria-hidden={true}
        >
            {
                rings.map((radius) => gridShape === "circle" ? <Circle
                    strokeWidth={measures.gridWidth}
                    stroke={gridColor}
                    cx={geometry.cx}
                    cy={geometry.cy}
                    key={radius}
                    fill="none"
                    r={radius}
                /> : <Path
                    d={getRadarChartPolygonPath(geometry.angles.map((_, index) => getRadarChartPoint({
                        layout: geometry,
                        radius,
                        index
                    })))}
                    strokeWidth={measures.gridWidth}
                    stroke={gridColor}
                    key={radius}
                    fill="none"
                />)
            }
            {
                geometry.angles.map((_, index) => {
                    const end = getRadarChartPoint({
                        radius: geometry.radius,
                        layout: geometry,
                        index
                    });

                    return <Line
                        strokeWidth={measures.gridWidth}
                        stroke={gridColor}
                        x1={geometry.cx}
                        y1={geometry.cy}
                        key={index}
                        x2={end.x}
                        y2={end.y}
                    />;
                })
            }
        </G>;
    };

    const renderPlot = (layout: ChartPlotLayout) => {
        const geometry = getLayout(layout);

        const drawables = getDrawables({
            geometry,
            layout
        });

        const activeIndex = layout.activeIndex;

        const fillOpacity = activeSeries.length >= 2 ? RADAR_CHART_MULTI_FILL_RATIO : 1;

        const activeEnd = activeIndex === undefined ? undefined : getRadarChartPoint({
            radius: geometry.radius,
            index: activeIndex,
            layout: geometry
        });

        return <>
            {renderGrid(geometry)}
            {
                isFilled ? drawables.map((item) => <Path
                    opacity={item.opacity * fillOpacity}
                    key={`fill-${item.key}`}
                    fill={item.areaColor}
                    stroke="none"
                    d={item.d}
                />) : null
            }
            {
                drawables.map((item) => <Path
                    strokeWidth={measures.lineWidth}
                    key={`line-${item.key}`}
                    strokeLinejoin="round"
                    opacity={item.opacity}
                    strokeLinecap="round"
                    stroke={item.color}
                    fill="none"
                    d={item.d}
                />)
            }
            {
                activeEnd ?
                    <Line
                        strokeWidth={measures.gridWidth}
                        stroke={spokeColor}
                        aria-hidden={true}
                        x1={geometry.cx}
                        y1={geometry.cy}
                        x2={activeEnd.x}
                        y2={activeEnd.y}
                    />
                :
                    null
            }
            {
                drawables.filter((item) => item.isLive).flatMap((item) => item.points.map((point, index) => isShowDots || index === activeIndex ? <G
                    key={`dot-${item.key}-${index}`}
                    opacity={item.opacity}
                    aria-hidden={true}
                >
                    <Circle
                        r={measures.dotRadius + measures.haloWidth}
                        fill={layout.surfaceColor}
                        cx={point.x}
                        cy={point.y}
                    />
                    <Circle
                        r={measures.dotRadius}
                        fill={item.color}
                        cx={point.x}
                        cy={point.y}
                    />
                </G> : null))
            }
            {
                geometry.labels.map((label, index) => <SvgText
                    fontWeight={index === activeIndex ? measures.labelWeight : measures.typography.fontWeight}
                    fill={index === activeIndex ? activeLabelColor : labelColor}
                    fontFamily={measures.typography.fontFamily}
                    fontSize={measures.typography.fontSize}
                    key={`${label.x}-${label.y}-${index}`}
                    textAnchor={label.anchor}
                    aria-hidden={true}
                    x={label.x}
                    y={label.y}
                >
                    {label.text}
                </SvgText>)
            }
        </>;
    };

    return <Chart.Plot
        isAnimatedOnMount={isAnimatedOnMountProp}
        tooltipBehaviour={tooltipBehaviour}
        seriesCount={activeSeries.length}
        onActiveChange={onActiveChange}
        pointCount={categories.length}
        renderTooltip={renderTooltip}
        customTheme={customTheme}
        style={[
            style,
            stylesheet.container
        ]}
        summary={currentSummary}
        getTooltip={getTooltip}
        getReading={getReading}
        getAnchor={getAnchor}
        plotSize={plotSize}
        getIndex={getIndex}
    >
        {renderPlot}
    </Chart.Plot>;
};
export default RadarChart;
